import { useEffect, useMemo,useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import ConfirmationPopup from "../../packages/ConfirmationPopup";
import CustomModal from "../../packages/CustomModal";
import IconButton from "../../packages/IconButton";
import WPDropdown from "../../packages/WPDropdown";
import WPInput from "../../packages/WPInput";
import WPTertiaryButton from "../../packages/WPTertiaryButton";

import DupontRadio from "../../components/DupontRadio";
import createNotification, {
  ERROR_NOTIFICATION,
  INFO_NOTIFICATION,
  SUCCESS_NOTIFICATION,
  WARNING_NOTIFICATION,
} from "../../components/Notifications";
import ColumnMappingHelpDialogs from "./ColumnMappingHelpDialogs";
import ColumnMappingModeSection from "./ColumnMappingModeSection";
import { formatUnitForDisplay } from "../../utils/Utils";
import {
  COLUMN_OPTIONS,
  COLUMN5_KEYS,
  COLUMN8_KEYS,
  NORMALIZATION_COLUMN_KEYS,
  NORMALIZATION_COLUMN_OPTIONS,
  TRAIN_OPTIONS,
} from "./columns.config";
import DataVisualizationTable from "./DataVisualizationTable";
import ImportColumnMapping from "./importColumnMapping";

import {
  COLUMN_SELECTION_VALUES,
  CustomModalButtons,
  FILE_SHEET_MAPPING_COLUMN_TYPES,
  LOCAL_STORAGE_KEYS,
  NORMALIZATION_TYPES,
  PASS_STAGE_KEYS,
  TROUBLESHOOTING_TOPICS,
} from "../../constants/global.constants";
import { COLUMN_MAPPING_ERROR_KEYS } from "../../constants/global.constants";

import { FTNormProIcon } from "../../assets/icons";
import { CheckCircleIcon, InfoCircle, QuestionIcon } from "../../assets/icons";

import CircularLoader from "../../common/styles/components/circularLoader";
import IconWithTooltip from "../../common/styles/components/headings/IconWithTooltip";

import { getLocalStorageItem, setLocalStorageItem } from "../../utils/session";
import {
  getDatasetDetails,
  isNullOrUndefinedOrEmpty,
  UpdateSetWithKey,
} from "../../utils/Utils";

import {
  fetchAutoDetectColumns,
  getDataVisualization,
  getReference,
  updateColumnMapping,
} from "../../network/cipAPI";

import "./index.scss";

const ColumnSelection = ({
  columnOptions,
  setColumnOptions,
  fileId,
  mappedColumn,
  setMappedColumn,
  mappedReference,
  setTempReferenceValues,
  setDatasetDetails,
  selectedRadioColumn,
  setSelectedRadioColumn,
  setMappedReference,
  setIsNormalizationColumnFilled,
  config,
  selectedTrain,
  setMandatoryComponentsStatus,
  normSelectionType,
  ownerUserName,
  plantReadinessStatus,
  colMappingType,
  uploadedInBlob,
}) => {
  const { t } = useTranslation();

  const {
    flow,
    pressure,
    feed_quality,
    permeate_quality,
    temperature,
    file_id,
    token,
  } = useSelector((state) => state);

  const unitSettings = useMemo(
    () => ({
      flow,
      pressure,
      feed_quality: formatUnitForDisplay(feed_quality),
      permeate_quality: formatUnitForDisplay(permeate_quality),
      temperature: formatUnitForDisplay(temperature),
    }),
    [flow, pressure, feed_quality, permeate_quality, temperature],
  );

  const columns = useMemo(
    () => COLUMN_OPTIONS(t, unitSettings),
    [t, unitSettings],
  );
  const normalizedColumns = useMemo(
    () => NORMALIZATION_COLUMN_OPTIONS(t, unitSettings),
    [t, unitSettings],
  );

  const [trainscolumns, setTrainscolumns] = useState([]);
  const [showDataVisualizationPopup, setshowDataVisualizationPopup] =
    useState(false);
  const [dataVisualisationRow, setDataVisualisationRow] = useState([]);
  const [dataVisualisationEnabled, setDataVisualisationEnabled] =
    useState(true);
  const [selectedColumns, setSelectedColumns] = useState({
    sel_train: 1,
    sel_pass: 1,
    sel_stage: 1,
  });
  const [changedSelectedColumns, setChangedSelectedColumns] = useState({});
  const [changedPassStageFields, setChangedPassStageFields] = useState({});
  const [showOverLayLoader, setShowOverLayLoader] = useState(false);
  const [normalizationOption, setNormalizationOption] = useState("ftnorm");
  const [normalizationChanged, setNormalizationChanged] = useState(false);
  const [showImportColumnMappingPopup, setShowImportColumnMappingPopup] =
    useState(false);
  const [trainsWithColSelection, setTrainsWithColSelection] = useState([]);
  const [showColumnMappingTroubleshoot, setShowColumnMappingTroubleshoot] =
    useState(false);
  const [showAutoDetectInfoPopup, setShowAutoDetectInfoPopup] = useState(false);
  const [showAutoDetectWarningPopup, setShowAutoDetectWarningPopup] =
    useState(false);

  const getOptimizedPayload = (
    mappedColumn,
    changedSelectedColumns,
    changedPassStageFields,
    selectedColumns,
  ) => {
    if (mappedColumn) {
      const changedKeys = Object.keys(changedSelectedColumns);
      const passOrStageChanged = PASS_STAGE_KEYS.some((key) =>
        changedKeys.includes(key),
      );
      return {
        ...changedSelectedColumns,
        ...(passOrStageChanged ? changedPassStageFields : {}),
      };
    } else {
      return selectedColumns;
    }
  };

  const shouldApplyAutoDetect = (columnName, autoDetectedColumns) => {
    if (COLUMN5_KEYS.includes(columnName)) {
      const userSelectedRadioOption = selectedRadioColumn.column5;
      if (userSelectedRadioOption === columnName) {
        return true;
      } else if (
        isNullOrUndefinedOrEmpty(
          autoDetectedColumns[userSelectedRadioOption]?.source_column,
        )
      ) {
        setSelectedRadioColumn((prev) => ({
          ...prev,
          column5: columnName,
        }));
        return true;
      } else {
        return false;
      }
    } else if (COLUMN8_KEYS.includes(columnName)) {
      const userSelectedRadioOption = selectedRadioColumn.column8;
      if (userSelectedRadioOption === columnName) {
        return true;
      } else if (
        isNullOrUndefinedOrEmpty(
          autoDetectedColumns[userSelectedRadioOption]?.source_column,
        )
      ) {
        setSelectedRadioColumn((prev) => ({
          ...prev,
          column8: columnName,
        }));
        return true;
      } else {
        return false;
      }
    } else if (NORMALIZATION_COLUMN_KEYS.includes(columnName)) {
      setNormalizationOption(COLUMN_SELECTION_VALUES.SELECT_COLUMNS);
    }
    return true;
  };

  const applyAutoDetectColumns = async () => {
    setShowOverLayLoader(true);
    try {
      const response = await fetchAutoDetectColumns(
        columnOptions,
        String(selectedTrain),
      );
      if (response && response.mapping) {
        const autoDetectedColumns = response.mapping;

        const tempSelectedColumn = { ...selectedColumns };
        const changedColumns = Object.entries(autoDetectedColumns).reduce(
          (acc, [columnName, detectedColData]) => {
            if (
              detectedColData?.source_column &&
              shouldApplyAutoDetect(columnName, autoDetectedColumns)
            ) {
              acc[columnName] = detectedColData.source_column;
            } else {
              //reset if not detected
              delete tempSelectedColumn[columnName];
            }

            return acc;
          },
          {},
        );

        setChangedSelectedColumns((prev) => ({
          ...prev,
          ...changedColumns,
        }));
        setSelectedColumns({ ...tempSelectedColumn, ...changedColumns });
        if (Object.keys(changedColumns).length > 0) {
          createNotification(
            INFO_NOTIFICATION,
            t("columnSelection.autoDetectSuccess"),
            "",
          );
        } else {
          createNotification(
            WARNING_NOTIFICATION,
            t("columnSelection.autoDetectFailed"),
            "",
          );
        }
      }
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setShowOverLayLoader(false);
    }
  };

  useEffect(() => {
    if (
      colMappingType === FILE_SHEET_MAPPING_COLUMN_TYPES.AUTO &&
      mappedColumn &&
      mappedColumn.date === undefined &&
      columnOptions.length > 0 &&
      uploadedInBlob
    ) {
      applyAutoDetectColumns();
    }
  }, [colMappingType, selectedTrain, uploadedInBlob, columnOptions]);

  useEffect(() => {
    const value =
      normSelectionType === NORMALIZATION_TYPES.CUSTOM
        ? COLUMN_SELECTION_VALUES.SELECT_COLUMNS
        : COLUMN_SELECTION_VALUES.FT_NORM;

    setNormalizationOption(value);
    setNormalizationChanged(false);
  }, [normSelectionType, selectedTrain]);

  // Reset normalization option when sheet is changed
  useEffect(() => {
    if (Array.isArray(mappedColumn) && mappedColumn.length === 0) {
      setNormalizationOption(COLUMN_SELECTION_VALUES.FT_NORM);
      setNormalizationChanged(false);
    }
  }, [mappedColumn]);

  useEffect(() => {
    setSelectedColumns({
      ...mappedColumn,
    });
    if (
      mappedColumn &&
      mappedColumn.hasOwnProperty("norm_permeate_flow") &&
      mappedColumn.hasOwnProperty("norm_pressure_drop") &&
      mappedColumn.hasOwnProperty("norm_salt_passage")
    ) {
      setIsNormalizationColumnFilled(true);
    }
    if (!Array.isArray(mappedColumn)) {
      const col5 =
        mappedColumn && mappedColumn.concentrate_flowrate
          ? "concentrate_flowrate"
          : "feed_flowrate";

      const col8 =
        mappedColumn && mappedColumn.pressure_drop
          ? "pressure_drop"
          : "concentrate_pressure";

      setSelectedRadioColumn({
        ...selectedRadioColumn,
        column5: col5,
        column8: col8,
      });
    }
  }, [mappedColumn]);

  useEffect(() => {
    if (typeof config === "object" && !Array.isArray(config)) {
      const { sel_pass, sel_stage } = selectedColumns;
      if (config?.trains.length >= 1) {
        setTrainscolumns(
          TRAIN_OPTIONS(
            t,
            config,
            selectedTrain > 0 ? selectedTrain - 1 : "",
            sel_pass > 0 ? sel_pass - 1 : "",
          ),
        );
      }

      if (
        typeof selectedColumns === "object" &&
        !Array.isArray(selectedColumns)
      ) {
        let num_pressure_vessels = "";
        let sel_element_involved = "";
        let ro_nf_element_used = "";

        if (selectedTrain && sel_pass && sel_stage) {
          const train = config?.trains.find(
            (op) => op.train_id === parseInt(selectedTrain),
          );
          const pass = train?.passes.find(
            (pass) => pass.pass_id === parseInt(selectedColumns.sel_pass),
          );

          if (pass) {
            if (sel_stage === "All") {
              let totalPressureVessels = 0;
              let totalNoPressureVessels = 0;
              const allElements = pass.stages.flatMap(
                (stage) => stage.ro_nf_element_used,
              );
              const uniqueElements = [...new Set(allElements)];

              pass?.stages.forEach((element) => {
                totalPressureVessels += +element.num_pressure_vessels;
                const pressureVessels = +element.num_pressure_vessels;
                const numElementsPerPressureVessel =
                  +element.num_element_per_pressure_vessel;

                const currentNoPressureVessels =
                  pressureVessels * numElementsPerPressureVessel;
                const cumulativeNoPressureVessels =
                  currentNoPressureVessels + totalNoPressureVessels;

                totalNoPressureVessels = cumulativeNoPressureVessels;
              });
              num_pressure_vessels = totalPressureVessels;
              sel_element_involved = totalNoPressureVessels;
              ro_nf_element_used =
                uniqueElements.length === 1
                  ? uniqueElements[0]
                  : COLUMN_SELECTION_VALUES.MULTIPLE_ELEMENTS;
            } else {
              const stage = pass?.stages.find(
                (stage) =>
                  stage.stage_id === parseInt(selectedColumns.sel_stage),
              );
              if (stage) {
                num_pressure_vessels = stage?.num_pressure_vessels || "";
                sel_element_involved =
                  stage?.num_pressure_vessels *
                    stage?.num_element_per_pressure_vessel || "";
                ro_nf_element_used = stage?.ro_nf_element_used || "";
                if (Array.isArray(ro_nf_element_used)) {
                  ro_nf_element_used = [...new Set(ro_nf_element_used)].join(
                    ", ",
                  );
                }
              } else {
                setSelectedColumns((prev) => ({
                  ...prev,
                  sel_stage: "",
                }));
                return;
              }
            }
          } else {
            setSelectedColumns((prev) => ({
              ...prev,
              sel_pass: "",
              sel_stage: "",
            }));
            return;
          }
        } else {
          num_pressure_vessels = "";
          sel_element_involved = "";
          ro_nf_element_used = "";
        }

        setSelectedColumns((prevSelectedColumns) => ({
          ...prevSelectedColumns,
          sel_pressure_vessels: num_pressure_vessels,
          sel_element_involved: sel_element_involved,
          sel_element_type: ro_nf_element_used,
        }));
        // Track auto-calculated field changes
        setChangedPassStageFields({
          sel_pressure_vessels: num_pressure_vessels,
          sel_element_involved: sel_element_involved,
          sel_element_type: ro_nf_element_used,
        });
      }
    }
  }, [
    config,
    t,
    selectedColumns["sel_train"],
    selectedColumns["sel_pass"],
    selectedColumns["sel_stage"],
  ]);

  useEffect(() => {
    if (!fileId) {
      setSelectedColumns([]);
      setDataVisualisationEnabled(true);
      setColumnOptions([]);
      setChangedSelectedColumns({});
      setChangedPassStageFields({});
      setNormalizationOption(COLUMN_SELECTION_VALUES.FT_NORM);
      setNormalizationChanged(false);
    }
  }, [fileId]);

  useEffect(() => {
    setChangedSelectedColumns({});
    setChangedPassStageFields({});
    setNormalizationChanged(false);
  }, [selectedTrain]);

  const onDropdownValChange = (value, key) => {
    setDataVisualisationEnabled(true);

    setSelectedColumns({ ...selectedColumns, [key]: value });
    setChangedSelectedColumns((prevSet) =>
      UpdateSetWithKey(prevSet, key, value),
    );
  };

  const onTrainsValChange = (value, key) => {
    if (key === "sel_train") {
      value = value ? value.toString() : "";

      setSelectedColumns({
        ...selectedColumns,
        [key]: value,
        sel_pass: "",
        sel_stage: "",
      });
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, key, value),
      );
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, ["sel_pass"], ""),
      );
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, ["sel_stage"], ""),
      );
    } else if (key === "sel_pass") {
      setSelectedColumns({
        ...selectedColumns,
        [key]: value ?? "",
        sel_stage: "",
      });
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, key, value ?? ""),
      );
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, ["sel_stage"], ""),
      );
    } else {
      setSelectedColumns({
        ...selectedColumns,
        [key]: value ?? "",
      });
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, key, value ?? ""),
      );
    }
  };

  const getKey = (column) => {
    const key =
      column.options === undefined
        ? column.key
        : selectedRadioColumn[column.key];
    return key;
  };

  const handleNormalizationOptionChange = (newOption) => {
    const normalizedFields = [
      "norm_permeate_flow",
      "norm_pressure_drop",
      "norm_salt_passage",
    ];
    // Clear normalized columns in selectedColumns
    setSelectedColumns((prev) => ({
      ...prev,
      ...Object.fromEntries(normalizedFields.map((field) => [field, ""])),
    }));
    // Clear normalized columns in changedSelectedColumns
    setChangedSelectedColumns((prev) => ({
      ...prev,
      ...Object.fromEntries(normalizedFields.map((field) => [field, ""])),
    }));
    setNormalizationOption(newOption);
    setNormalizationChanged(true);
  };

  // Format options for WPDropdown (ensures {label, value} shape)
  const formatOptionsForWPDropdown = (options) =>
    (options || []).map((option) =>
      typeof option === "object" && option.label && option.value
        ? option
        : { label: option, value: option },
    );

  // Get dropdown options, excluding those already selected elsewhere
  const getDropdownOptions = (currentValue) => {
    if (!columnOptions) return [];
    const selectedValues = Object.values(selectedColumns);
    return formatOptionsForWPDropdown(
      Object.values(columnOptions).filter(
        (opt) => !selectedValues.includes(opt) || opt === currentValue,
      ),
    );
  };

  const handleOptionChange = (event) => {
    setDataVisualisationEnabled(true);
    const { name, value } = event.target;
    const prevKey = selectedRadioColumn[name];
    setSelectedColumns((prev) => {
      const next = { ...prev };
      delete next[prevKey];
      return next;
    });

    setChangedSelectedColumns((prev) => {
      const next = { ...prev };
      delete next[prevKey];
      return next;
    });
    setSelectedRadioColumn({ ...selectedRadioColumn, [name]: value });
  };

  const getchecked = (column, key) => {
    return selectedRadioColumn[column] === key;
  };

  const openDataVisualizationPopup = async () => {
    setShowOverLayLoader(true);
    try {
      const response = await getDataVisualization(fileId, selectedTrain);
      if (response && response.data) {
        if (response.data.detail && response.data.detail.error) {
          throw new Error("DATA_VISUALIZATION_ERROR");
        } else {
          setDataVisualisationRow(response.data);
        }
      }
    } catch (err) {
      setDataVisualisationRow([]);
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setShowOverLayLoader(false);
      return setshowDataVisualizationPopup(true);
    }
  };

  const getTroubleshootingReadTopics = () =>
    JSON.parse(
      getLocalStorageItem(LOCAL_STORAGE_KEYS.TROUBLESHOOTING_READ_TOPICS) ||
        "[]",
    );

  const hasReadAutoDetectTroubleshooting = (troubleshootingReadTopics) =>
    troubleshootingReadTopics.includes(
      TROUBLESHOOTING_TOPICS.AUTO_DETECT_COLUMNS,
    );

  const handleAutoDetectColumnsClick = () => {
    const troubleshootingReadTopics = getTroubleshootingReadTopics();

    if (hasReadAutoDetectTroubleshooting(troubleshootingReadTopics)) {
      setShowAutoDetectWarningPopup(true);
      return;
    }

    setShowAutoDetectInfoPopup(true);
  };

  const handleAutoDetectInfoClose = ({ target }) => {
    const action = target?.id;

    const troubleshootingReadTopics = getTroubleshootingReadTopics();

    if (!hasReadAutoDetectTroubleshooting(troubleshootingReadTopics)) {
      const updatedTroubleshootingReadTopics = [
        ...troubleshootingReadTopics,
        TROUBLESHOOTING_TOPICS.AUTO_DETECT_COLUMNS,
      ];

      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.TROUBLESHOOTING_READ_TOPICS,
        JSON.stringify(updatedTroubleshootingReadTopics),
      );
    }

    if (action === CustomModalButtons.CONFIRM) {
      setShowAutoDetectInfoPopup(false);
      setShowAutoDetectWarningPopup(true);
      return;
    }

    setShowAutoDetectInfoPopup(false);
  };

  const handleAutoDetectWarningClose = (action) => {
    setShowAutoDetectWarningPopup(false);
    setShowAutoDetectInfoPopup(false);

    if (action === CustomModalButtons.CONFIRM) {
      applyAutoDetectColumns();
    }
  };

  const openColumnMappingTroubleshoot = () => {
    setShowColumnMappingTroubleshoot(true);
  };

  const closeColumnMappingTroubleshoot = () => {
    setShowColumnMappingTroubleshoot(false);
  };

  const onCloseClicked = async (value) => {
    setshowDataVisualizationPopup(false);
  };

  const checkAllRequiredFieldsFilled = () => {
    const {
      date,
      feed_flowrate,
      concentrate_flowrate,
      pressure_drop,
      concentrate_pressure,
      feed_quality,
      permeate_flowrate,
      feed_pressure,
      permeate_quality,
      feed_temp,
      permeate_pressure,
      norm_permeate_flow,
      norm_pressure_drop,
      norm_salt_passage,
    } = selectedColumns;

    const hasAllInputs = !!(
      date &&
      permeate_flowrate &&
      feed_temp &&
      permeate_pressure &&
      (feed_flowrate || concentrate_flowrate) &&
      permeate_quality &&
      feed_pressure &&
      feed_quality &&
      (concentrate_pressure || pressure_drop)
    );

    // If "Select Columns" is selected, normalized columns are mandatory
    if (normalizationOption === COLUMN_SELECTION_VALUES.SELECT_COLUMNS) {
      return (
        hasAllInputs &&
        !!(norm_permeate_flow && norm_pressure_drop && norm_salt_passage)
      );
    }

    return hasAllInputs;
  };

  const checkChangedFields = () => {
    return (
      Object.keys(changedSelectedColumns).length > 0 || normalizationChanged
    );
  };

  const getConfirmButtonState = () => {
    let disableConfirmButton = true;

    const hasAllRequiredFields = checkAllRequiredFieldsFilled();
    const hasChangedFields = checkChangedFields();

    if (hasAllRequiredFields && hasChangedFields) {
      disableConfirmButton = false;
    }

    return disableConfirmButton;
  };

  const onConfirmColumn = async () => {
    setShowOverLayLoader(true);
    try {
      const payload = {
        ...getOptimizedPayload(
          mappedColumn,
          changedSelectedColumns,
          changedPassStageFields,
          selectedColumns,
        ),
      };

      // Send norm_selection_type if: first time mapping (key not in mappedColumn) OR normalization was changed
      const isFirstTimeMapping =
        !mappedColumn || !mappedColumn.hasOwnProperty("norm_selection_type");
      if (isFirstTimeMapping || normalizationChanged) {
        payload.norm_selection_type =
          normalizationOption === COLUMN_SELECTION_VALUES.FT_NORM
            ? NORMALIZATION_TYPES.FT_NORM_PRO
            : NORMALIZATION_TYPES.CUSTOM;

        // Remove normalized columns from payload if FT Norm Pro is selected
        if (normalizationOption === COLUMN_SELECTION_VALUES.FT_NORM) {
          delete payload.norm_permeate_flow;
          delete payload.norm_pressure_drop;
          delete payload.norm_salt_passage;
        }
      }

      const response = await updateColumnMapping(
        fileId,
        payload,
        selectedTrain,
      );
      if (response && response.detail === undefined) {
        setMappedColumn(selectedColumns);
        const { min_date, max_date } = response;
        setDatasetDetails(
          getDatasetDetails({
            owner_username: ownerUserName,
            min_date,
            max_date,
          }),
        );
        // Check if response.ref is empty, if so call getReference API
        if (!response.ref || response.ref === "") {
          try {
            const referenceResponse = await getReference(selectedTrain);
            if (referenceResponse) {
              setMappedReference(referenceResponse.data);
            } else {
              setMappedReference(response.ref);
            }
          } catch (referenceErr) {
            createNotification(
              ERROR_NOTIFICATION,
              t("notificationStrings.somethingWrong"),
              "",
            );
            setMappedReference(response.ref);
          }
        } else {
          setMappedReference(response.ref);
        }

        setDataVisualisationEnabled(true);
        setChangedSelectedColumns({});
        setChangedPassStageFields({});
        setNormalizationChanged(false);
        createNotification(
          SUCCESS_NOTIFICATION,
          t("analyseData.columnMapped"),
          "",
        );
        setDataVisualisationEnabled(false);
        if (
          payload.hasOwnProperty("norm_permeate_flow") &&
          payload.hasOwnProperty("norm_pressure_drop") &&
          payload.hasOwnProperty("norm_salt_passage")
        ) {
          setIsNormalizationColumnFilled(true);
        }
      } else if (
        response &&
        response.detail &&
        response.detail.key === COLUMN_MAPPING_ERROR_KEYS.NO_VALID_REF_CONDITION
      ) {
        setDataVisualisationEnabled(false);
        setChangedPassStageFields({});
        setChangedSelectedColumns({});
        setNormalizationChanged(false);
        createNotification(
          SUCCESS_NOTIFICATION,
          t("analyseData.columnMapped"),
          "",
        );
        createNotification(
          WARNING_NOTIFICATION,
          t("analyseData.noValidReferenceCondition"),
          "",
        );
      } else {
        throw new Error("UNKNOWN_ERROR");
      }
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setShowOverLayLoader(false);
    }
  };

  const getTrainValue = (value) => {
    if (value !== undefined && value !== null) {
      if (typeof value === "boolean" || typeof value === "number") {
        return value.toString();
      }
      return value;
    }
  };

  const handleImportColumnMapping = (
    columnMapping,
    normalizationColumnMapping,
  ) => {
    const changedColumns = {};
    const tempSelectedColumns = { ...selectedColumns };

    columnMapping.forEach((mapping) => {
      if (columnOptions && columnOptions.includes(mapping.mappedTo)) {
        if (mapping.radioKey) {
          setSelectedRadioColumn((prev) => ({
            ...prev,
            [mapping.radioKey]: mapping.key,
          }));
        }
        changedColumns[mapping.key] = mapping.mappedTo;
      } else {
        if (mapping.radioKey) {
          const otherRadioOption = mapping.options.find(
            (opt) => opt !== mapping.key,
          );
          delete changedColumns[otherRadioOption];
          delete tempSelectedColumns[otherRadioOption];
        }
        delete changedColumns[mapping.key];
        delete tempSelectedColumns[mapping.key];
      }
    });

    if (normalizationColumnMapping.length > 1) {
      setNormalizationOption(COLUMN_SELECTION_VALUES.SELECT_COLUMNS);
      normalizationColumnMapping.forEach((mapping) => {
        if (columnOptions && columnOptions.includes(mapping.mappedTo)) {
          changedColumns[mapping.key] = mapping.mappedTo;
        } else {
          delete changedColumns[mapping.key];
          delete tempSelectedColumns[mapping.key];
        }
      });
    } else {
      NORMALIZATION_COLUMN_KEYS.forEach((normField) => {
        delete tempSelectedColumns[normField];
      });
      setNormalizationOption(COLUMN_SELECTION_VALUES.FT_NORM);
    }

    setChangedSelectedColumns((prev) => ({
      ...prev,
      ...changedColumns,
    }));
    setSelectedColumns({ ...tempSelectedColumns, ...changedColumns });
    setShowImportColumnMappingPopup(false);
    if (Object.keys(changedColumns).length > 0) {
      createNotification(
        INFO_NOTIFICATION,
        t("columnSelection.importColumnsSuccess"),
        "",
      );
    } else {
      createNotification(
        WARNING_NOTIFICATION,
        t("columnSelection.noColumnsImported"),
        "",
      );
    }
  };

  const fetchColumnSelectionValidTrains = async () => {
    setTrainsWithColSelection([]);

    if (plantReadinessStatus.length > 1) {
      const filteredData = plantReadinessStatus.filter(
        (trainData) =>
          trainData.column_mapping === true &&
          trainData.train !== selectedTrain,
      );

      if (filteredData.length > 0) {
        setTrainsWithColSelection(
          filteredData.map((trainData) => trainData.train),
        );
      }
    }
  };

  useEffect(() => {
    if (selectedTrain && token) fetchColumnSelectionValidTrains();
  }, [selectedTrain, token, plantReadinessStatus]);

  useEffect(() => {
    const hasAllRequiredFields = checkAllRequiredFieldsFilled();
    const hasChangedFields = checkChangedFields();

    const columnMappingStatus = {
      fulfilled: hasAllRequiredFields,
      changed: hasChangedFields,
      error: false,
    };
    setMandatoryComponentsStatus((prevStatus) => ({
      ...prevStatus,
      columnMapping: columnMappingStatus,
    }));
  }, [
    selectedColumns,
    changedSelectedColumns,
    normalizationChanged,
    normalizationOption,
  ]);

  return (
    <div className="ro-card-wrapper">
      {showOverLayLoader && (
        <div className="circular-loader-overlay">
          <CircularLoader />
        </div>
      )}
      <div className="ro-card-header">
        <div className="info-section">
          <div className="teal-heading">{t("analyseData.columnMapping")}</div>
          <IconWithTooltip label={t("helpInformation.columnSelectionHelp")} />
        </div>
        <div className="column-mapping-header-actions">
          <WPTertiaryButton
            variant="text"
            label={t("analyseData.viewData")}
            onClick={openDataVisualizationPopup}
            disabled={dataVisualisationEnabled}
            size="small"
          />
          <WPTertiaryButton
            variant="outlined"
            icon={<CheckCircleIcon />}
            iconPlacement="before"
            label={t("ValueStrings.confirm")}
            disabled={getConfirmButtonState()}
            onClick={onConfirmColumn}
            size="small"
          />
          <IconButton
            onClick={openColumnMappingTroubleshoot}
            className="column-mapping-troubleshoot-icon-btn"
          >
            <QuestionIcon />
          </IconButton>
        </div>
      </div>
      <div className="grid-container-trains">
        {Object.values(trainscolumns).map((column) => {
          const { key, name, options } = column;
          const isInputField = [
            "sel_pressure_vessels",
            "sel_element_involved",
            "sel_element_type",
          ].includes(key);

          // Skip input fields - they'll be rendered together below
          if (isInputField) return null;

          // Render Pass and Stage selects
          return (
            <div key={key} className="train-field-select">
              <WPDropdown
                label={name}
                options={formatOptionsForWPDropdown(options)}
                value={getTrainValue(selectedColumns[key])}
                onChange={(selectedOption) =>
                  onTrainsValChange(selectedOption?.value, key)
                }
                showCancelIcon
              />
            </div>
          );
        })}

        {/* Render grouped input fields */}
        <div className="train-fields-group">
          {[
            "sel_pressure_vessels",
            "sel_element_involved",
            "sel_element_type",
          ].map((fieldKey) => {
            const field = Object.values(trainscolumns).find(
              (col) => col.key === fieldKey,
            );
            if (!field) return null;

            const displayValue =
              fieldKey === "sel_element_type" &&
              selectedColumns[fieldKey] ===
                COLUMN_SELECTION_VALUES.MULTIPLE_ELEMENTS
                ? t("ValueStrings.multipleElements")
                : selectedColumns[fieldKey] || "";

            return (
              <div key={fieldKey} className="train-field-input">
                <WPInput
                  id={fieldKey}
                  label={field.name}
                  value={displayValue}
                  disabled
                />
              </div>
            );
          })}
        </div>
      </div>

      <ColumnMappingModeSection
        file_id={file_id}
        trainsWithColSelection={trainsWithColSelection}
        onAutoDetectColumnsClick={handleAutoDetectColumnsClick}
        onImportColumnMappingClick={() => setShowImportColumnMappingPopup(true)}
      />

      <div className="container-padding-column">
        <div className="subHeading">
          {t("analysisColumn.feedPermeateDataColumns")}
        </div>
        <div className="grid-container-column">
          {Object.entries(columns).map(([columnKey, column]) => {
            const columnKeyValue = getKey(column);
            const selectedValue = selectedColumns[columnKeyValue];

            // Render columns with radio options
            if (column.options) {
              const options = Object.values(column.options);
              const radioLabels =
                columnKey === "column5"
                  ? ["Feed", "Concentrate"]
                  : ["dP", "Concentrate"];

              return (
                <div key={columnKey} className="grid-item-column-with-radio">
                  <div className="grid-item-column">
                    <WPDropdown
                      label={column.label}
                      value={selectedValue}
                      options={getDropdownOptions(selectedValue)}
                      onChange={(selectedOption) =>
                        onDropdownValChange(
                          selectedOption?.value,
                          columnKeyValue,
                        )
                      }
                      required
                      showCancelIcon
                    />
                  </div>
                  <div
                    className="radio-group-inline"
                    onChange={handleOptionChange}
                  >
                    {options.map((option, idx) => (
                      <DupontRadio
                        key={option.key}
                        text={t(`columnSelection.${radioLabels[idx]}`)}
                        fontsize={"font-14"}
                        checked={getchecked(columnKey, option.key)}
                        value={option.key}
                        name={columnKey}
                      />
                    ))}
                  </div>
                </div>
              );
            }

            // Render regular columns
            return (
              <div key={columnKey} className="grid-item-column">
                <WPDropdown
                  label={column.name}
                  value={selectedValue}
                  options={getDropdownOptions(selectedValue)}
                  onChange={(selectedOption) =>
                    onDropdownValChange(selectedOption?.value, columnKeyValue)
                  }
                  required
                  showCancelIcon
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="normalization-section">
        <div className="subHeading">
          <div className="info-section">
            {t("analysisColumn.normalizationTitle")}
            <IconWithTooltip
              label={t("analysisColumn.normalizationSectionDescription")}
            />
          </div>
        </div>

        <div
          className="normalization-radio-group"
          onChange={(event) => {
            const target = event.target;
            if (target.type === "radio") {
              handleNormalizationOptionChange(target.value);
            }
          }}
        >
          <DupontRadio
            text={t("analysisColumn.ftNormProLabel")}
            fontsize={"font-14"}
            checked={normalizationOption === COLUMN_SELECTION_VALUES.FT_NORM}
            value={COLUMN_SELECTION_VALUES.FT_NORM}
            name="normalization"
          />
          <DupontRadio
            text={t("analysisColumn.selectColumnsLabel")}
            fontsize={"font-14"}
            checked={
              normalizationOption === COLUMN_SELECTION_VALUES.SELECT_COLUMNS
            }
            value={COLUMN_SELECTION_VALUES.SELECT_COLUMNS}
            name="normalization"
          />
        </div>

        {normalizationOption === COLUMN_SELECTION_VALUES.FT_NORM ? (
          <div className="ftnorm-info-box">
            <div className="ftnorm-info-content">
              <InfoCircle />
              <span className="info-text">
                {t("analysisColumn.ftNormInfoText")}
              </span>
            </div>
            <FTNormProIcon />
          </div>
        ) : (
          <div>
            <div className="normalization-columns-grid">
              {Object.keys(normalizedColumns).map((normalizedColumn) => (
                <div
                  key={normalizedColumn}
                  className="normalization-column-item"
                >
                  <WPDropdown
                    label={normalizedColumns[normalizedColumn].name}
                    value={
                      selectedColumns[
                        getKey(normalizedColumns[normalizedColumn])
                      ]
                    }
                    options={getDropdownOptions(
                      selectedColumns[
                        getKey(normalizedColumns[normalizedColumn])
                      ],
                    )}
                    onChange={(selectedOption) =>
                      onDropdownValChange(
                        selectedOption?.value,
                        getKey(normalizedColumns[normalizedColumn]),
                      )
                    }
                    required
                    showCancelIcon
                  />
                </div>
              ))}
            </div>
            <div className="normalization-note">
              <InfoCircle />
              <span className="column-note-text">
                {t("analysisColumn.normalizationNote")}
              </span>
            </div>
          </div>
        )}
      </div>

      <div>
        {showColumnMappingTroubleshoot && (
          <ColumnMappingHelpDialogs
            closeColumnMappingTroubleshoot={closeColumnMappingTroubleshoot}
          />
        )}

        {showAutoDetectInfoPopup && (
          <CustomModal
            header={t("helpInformation.autoDetectColumnsModalHeader")}
            onModalClose={handleAutoDetectInfoClose}
            confirmBtn={t("ValueStrings.okay")}
            width="700px"
            hideCloseIcon
          >
            <div className="auto-detect-info-content">
              <ul className="auto-detect-info-list">
                <li>{t("helpInformation.autoDetectInfoPoint1")}</li>
                <li>
                  {t("helpInformation.autoDetectInfoPoint2")}
                  <ul className="column-mapping-troubleshoot-dialog__sub-list">
                    <li>{t("helpInformation.autoDetectInfoPoint3")}</li>
                    <li>{t("helpInformation.autoDetectInfoPoint4")}</li>
                  </ul>
                </li>
                <li>{t("helpInformation.autoDetectInfoPoint5")}</li>
              </ul>
            </div>
          </CustomModal>
        )}

        {showAutoDetectWarningPopup && (
          <ConfirmationPopup
            type="danger"
            header={t("columnSelection.autoDetectWarningHeader")}
            description={t("columnSelection.autoDetectWarningDescription")}
            confirmBtn={t("columnSelection.autoDetectWarningConfirm")}
            cancelBtn={t("ValueStrings.cancel")}
            onClose={handleAutoDetectWarningClose}
            width="480px"
          />
        )}

        {showDataVisualizationPopup && (
          <DataVisualizationTable
            open={showDataVisualizationPopup}
            title={t("analyseData.viewData")}
            rows={dataVisualisationRow}
            setshowDataVisualizationPopup={setshowDataVisualizationPopup}
            setTempReferenceValues={setTempReferenceValues}
            onClose={onCloseClicked}
            description={t("analyseData.dataVisualizationDescription")}
          />
        )}
        {showImportColumnMappingPopup && (
          <ImportColumnMapping
            trains={trainsWithColSelection}
            columns={columns}
            normalizedColumns={normalizedColumns}
            onImportColumnMapping={handleImportColumnMapping}
            onModalClose={() => setShowImportColumnMappingPopup(false)}
          />
        )}
      </div>
    </div>
  );
};
export default ColumnSelection;
